(() => {
  const host = document.querySelector('[data-ff-wishlist-prompt]');
  if (!host || host.dataset.initialized === 'true') return;
  host.dataset.initialized = 'true';
  const storageKey = 'faimly-wishlist-login-seen-v2';
  const intro = host.querySelector('[data-ff-wishlist-intro]');
  const trigger = host.querySelector('[data-ff-wishlist-login-trigger]');
  const fallback = host.querySelector('[data-ff-wishlist-fallback]');
  const fallbackIntro = host.querySelector('[data-ff-wishlist-fallback-intro]');
  let seenInMemory = false;
  let pending = false;
  let subscribed = false;
  let otpSignedIn = false;
  let observer = null;
  let fallbackTimer = null;
  let contextActive = false;
  let nativeOpened = false;
  let previousFocus = null;
  let waitingForVisibility = false;
  let sessionCheck = null;
  let sessionCheckedAt = 0;
  const watchedButtons = new WeakSet();
  const buttonSelector = 'button[data-in-wishlist]';
  const refreshSession = () => {
    if (!sessionCheck || Date.now() - sessionCheckedAt > 30000) {
      sessionCheckedAt = Date.now();
      sessionCheck = Promise.resolve().then(() => window.ffAccountSession?.refresh()).catch(() => null);
    }
    return sessionCheck;
  };
  const isVisible = element => {
    if (!element || !element.isConnected || document.hidden) return false;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    }
    return element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0;
  };

  const hasSeen = () => {
    if (seenInMemory) return true;
    try { if (localStorage.getItem(storageKey) === 'seen') return true; } catch (_) {}
    try { return sessionStorage.getItem(storageKey) === 'seen'; } catch (_) { return false; }
  };
  const markSeen = () => {
    seenInMemory = true;
    try { localStorage.setItem(storageKey, 'seen'); } catch (_) {}
    try { sessionStorage.setItem(storageKey, 'seen'); } catch (_) {}
    window.dispatchEvent(new Event('ff:wishlist-prompt'));
  };
  const isSignedIn = () => host.dataset.signedIn === 'true' || window.ffAccountSession?.isSignedIn() === true;
  const restoreFocus = () => {
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({preventScroll: true});
  };
  const clearContext = () => {
    contextActive = false;
    waitingForVisibility = false;
    pending = false;
    nativeOpened = false;
    clearTimeout(fallbackTimer);
    observer?.disconnect();
    observer = null;
  };
  const showFallback = () => {
    if (isSignedIn() || !contextActive) { clearContext(); return; }
    if (!fallback || typeof fallback.showModal !== 'function') { clearContext(); return; }
    if (!fallbackIntro.childElementCount) fallbackIntro.append(intro.content.cloneNode(true));
    if (document.hidden) { waitingForVisibility = true; return; }
    if (!fallback.open) fallback.showModal();
    if (!isVisible(fallback)) { clearContext(); return; }
    markSeen();
    pending = false;
  };
  const syncNative = root => {
    if (!contextActive) return;
    const modal = root.querySelector('[role="dialog"]');
    if (!modal) {
      if (nativeOpened) { clearContext(); restoreFocus(); }
      return;
    }
    if (modal.querySelector('.otp-account-dashboard')) {
      modal.querySelector('.otp-account-modal__close')?.click();
      clearContext();
      return;
    }
    const card = modal.querySelector('.otp-card');
    if (!card || !isVisible(modal)) return;
    const entry = !!card.querySelector('input[type="tel"], input[type="email"]') &&
      !card.querySelector('input[autocomplete="one-time-code"]');
    if (!nativeOpened) {
      nativeOpened = true;
      clearTimeout(fallbackTimer);
      if (fallback?.open) fallback.close();
      markSeen();
      pending = false;
    }
    modal.classList.add('ff-wishlist-login');
    modal.classList.toggle('ff-wishlist-login--entry', entry);
    if (entry) {
      modal.setAttribute('aria-label', host.dataset.title);
      if (!card.querySelector('.ff-wishlist-intro')) card.prepend(intro.content.cloneNode(true));
    } else {
      const title = card.querySelector('.otp-card__title');
      if (title?.textContent) modal.setAttribute('aria-label', title.textContent);
    }
  };
  const openPrompt = async () => {
    if (pending && contextActive) return;
    contextActive = true;
    previousFocus = document.activeElement;
    markSeen();
    if (fallback?.open) fallback.close();
    observer?.disconnect();
    try {
      const account = window.ffAccountSession;
      if (account?.requestLogin) await account.requestLogin(location.pathname + location.search);
    } finally { clearContext(); }
  };
  const onItemCreated = async () => {
    if (pending || contextActive) return;
    if (hasSeen()) { window.dispatchEvent(new Event('ff:wishlist-prompt')); return; }
    if (isSignedIn()) return;
    pending = true;
    await refreshSession();
    if (hasSeen() || isSignedIn()) { pending = false; return; }
    if (document.hidden) { waitingForVisibility = true; return; }
    openPrompt();
  };
  const watchAddition = event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    const button = event.composedPath().find(node => node instanceof Element && node.matches(buttonSelector));
    if (!button || button.dataset.inWishlist !== 'false' || button.disabled || button.getAttribute('aria-disabled') === 'true' || watchedButtons.has(button)) return;
    if (hasSeen()) { window.dispatchEvent(new Event('ff:wishlist-prompt')); return; }
    watchedButtons.add(button);
    void refreshSession();
    const container = button.parentElement;
    let timer;
    let settled = false;
    const stop = () => { settled = true; watcher.disconnect(); clearTimeout(timer); watchedButtons.delete(button); };
    const check = () => {
      if (settled) return;
      const current = button.isConnected ? button : container.querySelector(buttonSelector);
      if (!current || current.dataset.inWishlist !== 'true' || current.disabled || current.getAttribute('aria-disabled') === 'true') return;
      stop();
      void onItemCreated();
    };
    const watcher = new MutationObserver(check);
    watcher.observe(container, {childList: true, subtree: true, attributes: true, attributeFilter: ['data-in-wishlist', 'disabled', 'aria-disabled', 'class']});
    timer = setTimeout(stop, 20000);
    queueMicrotask(check);
    onSdkReady();
  };
  const onSdkReady = () => {
    const sdk = window.gw?.sdk;
    if (!sdk?.runIfModuleReady) return;
    try {
      sdk.runIfModuleReady(sdk.common.constants.modules.WISHLISTS, () => {
        if (subscribed) return;
        sdk.on(sdk.wishlists.events.WISHLIST_ITEM_CREATED, onItemCreated);
        subscribed = true;
      });
    } catch (_) {}
  };
  trigger?.addEventListener('click', event => event.preventDefault());
  host.querySelectorAll('[data-ff-wishlist-close]').forEach(button => {
    button.addEventListener('click', () => fallback.close());
  });
  fallback?.addEventListener('close', () => {
    if (!nativeOpened) { clearContext(); restoreFocus(); }
  });
  fallback?.addEventListener('click', event => {
    if (event.target !== fallback) return;
    const rect = fallback.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) fallback.close();
  });
  window.addEventListener('ff:account-session', event => {
    otpSignedIn = event.detail?.signedIn === true;
    sessionCheck = null;
    if (fallback?.open) fallback.close();
  });
  window.addEventListener('otp:external-logout', () => { otpSignedIn = false; sessionCheck = null; });
  document.addEventListener('click', watchAddition, true);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden || !waitingForVisibility) return;
    waitingForVisibility = false;
    if (contextActive) showFallback();
    else { pending = false; void onItemCreated(); }
  });
  document.addEventListener('gwSdkReady', onSdkReady);
  onSdkReady();
  if (!hasSeen() && host.dataset.signedIn !== 'true' && window.OTP_PLUS_CUSTOMER?.logged_in !== true) void refreshSession();
})();

